import { forwardRef } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { READING } from "@/constants/testIds";

/**
 * ShareCard — off-screen printable/exportable composition (light theme).
 * Fixed width 1080px, dynamic height. Captured to PNG via html-to-image.
 */
export const ShareCard = forwardRef(function ShareCard(
  { consultant, question, picks, interpretation, createdAt },
  ref
) {
  const { t, language } = useLanguage();

  const dateStr = createdAt
    ? new Date(createdAt).toLocaleDateString(language === "fr" ? "fr-FR" : "en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : "";

  const byPos = Object.fromEntries((picks || []).map((p) => [p.position, p]));

  const grid = [
    { row: 0, col: 1, key: "top" },
    { row: 1, col: 0, key: "left" },
    { row: 1, col: 1, key: "center" },
    { row: 1, col: 2, key: "right" },
    { row: 2, col: 1, key: "bottom" },
  ];

  return (
    <div
      ref={ref}
      data-testid={READING.shareCard}
      style={{
        width: "1080px",
        backgroundColor: "#F4EAD1",
        color: "#14103A",
        padding: "72px 72px 60px",
        fontFamily: "'Cormorant Garamond', serif",
        boxSizing: "border-box",
        border: "2px solid #8A5A0F",
        position: "relative",
      }}
    >
      <div
        style={{
          position: "absolute",
          inset: "14px",
          border: "1px solid rgba(138,90,15,0.4)",
          pointerEvents: "none",
        }}
      />

      {/* Header */}
      <div style={{ textAlign: "center", marginBottom: "40px" }}>
        <div
          style={{
            letterSpacing: "0.6em",
            fontSize: "14px",
            color: "#8A5A0F",
            fontFamily: "'Outfit', sans-serif",
            fontWeight: 500,
            textTransform: "uppercase",
          }}
        >
          ✦ ARCANA MYSTIQUE ✦
        </div>
        <div
          style={{
            fontStyle: "italic",
            fontWeight: 300,
            fontSize: "56px",
            lineHeight: 1.1,
            marginTop: "16px",
            color: "#14103A",
          }}
        >
          {t.reading.phase.result}
        </div>
        <div
          style={{
            marginTop: "10px",
            letterSpacing: "0.35em",
            fontSize: "12px",
            color: "rgba(20,16,58,0.6)",
            fontFamily: "'Outfit', sans-serif",
            textTransform: "uppercase",
          }}
        >
          {dateStr}
        </div>
        <div
          style={{
            width: "220px",
            height: "1px",
            margin: "24px auto 0",
            background:
              "linear-gradient(90deg, transparent, rgba(138,90,15,0.8), transparent)",
          }}
        />
      </div>

      <div
        style={{
          textAlign: "center",
          fontStyle: "italic",
          fontSize: "22px",
          lineHeight: 1.5,
          color: "rgba(20,16,58,0.85)",
          margin: "0 auto 40px",
          maxWidth: "820px",
        }}
      >
        {question
          ? `« ${question} »`
          : language === "fr"
          ? "— Sagesse du tarot —"
          : "— Wisdom of the tarot —"}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "260px 1fr",
          gap: "48px",
          alignItems: "start",
          marginBottom: "44px",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              letterSpacing: "0.35em",
              fontSize: "12px",
              color: "#8A5A0F",
              fontFamily: "'Outfit', sans-serif",
              textTransform: "uppercase",
              marginBottom: "12px",
            }}
          >
            {t.history.consultantLabel}
          </div>
          <MiniCard card={consultant} language={language} size="lg" />
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 1fr)",
            gridTemplateRows: "repeat(3, auto)",
            gap: "18px",
            justifyItems: "center",
            alignItems: "center",
          }}
        >
          {[0, 1, 2].flatMap((row) =>
            [0, 1, 2].map((col) => {
              const cell = grid.find((g) => g.row === row && g.col === col);
              if (!cell) return <div key={`${row}-${col}`} />;
              const p = byPos[cell.key];
              return (
                <div key={cell.key} style={{ textAlign: "center" }}>
                  <div
                    style={{
                      letterSpacing: "0.3em",
                      fontSize: "10px",
                      color: "#8A5A0F",
                      fontFamily: "'Outfit', sans-serif",
                      textTransform: "uppercase",
                      marginBottom: "6px",
                    }}
                  >
                    {t.reading.posShort[cell.key]}
                  </div>
                  <MiniCard card={p?.card} language={language} size="sm" />
                </div>
              );
            })
          )}
        </div>
      </div>

      <div
        style={{
          width: "100%",
          height: "1px",
          background:
            "linear-gradient(90deg, transparent, rgba(138,90,15,0.55), transparent)",
          marginBottom: "32px",
        }}
      />

      <div
        style={{
          fontSize: "20px",
          lineHeight: 1.7,
          color: "#14103A",
          whiteSpace: "pre-wrap",
          fontFamily: "'Cormorant Garamond', serif",
        }}
      >
        {interpretation}
      </div>

      <div
        style={{
          textAlign: "center",
          marginTop: "56px",
          paddingTop: "20px",
          borderTop: "1px solid rgba(138,90,15,0.3)",
          letterSpacing: "0.32em",
          fontSize: "11px",
          color: "rgba(20,16,58,0.55)",
          fontFamily: "'Outfit', sans-serif",
          textTransform: "uppercase",
        }}
      >
        {t.footer}
      </div>
    </div>
  );
});

const MiniCard = ({ card, language, size = "sm" }) => {
  const dims =
    size === "lg"
      ? { w: 220, h: 340, name: 20, glyph: 68, roman: 18 }
      : { w: 150, h: 232, name: 14, glyph: 44, roman: 13 };

  const hasImage = !!(card && card.image);

  return (
    <div
      style={{
        width: `${dims.w}px`,
        height: `${dims.h}px`,
        margin: "0 auto",
        background: hasImage
          ? "#F4EAD1"
          : "radial-gradient(circle at 50% 30%, rgba(138,90,15,0.14) 0%, rgba(138,90,15,0) 55%), linear-gradient(180deg, #FAF3E0 0%, #EADFC0 100%)",
        border: "1px solid #8A5A0F",
        borderRadius: "8px",
        padding: hasImage ? "0" : "10px",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        alignItems: "center",
        overflow: "hidden",
        boxShadow: "0 10px 24px -12px rgba(20,16,58,0.35)",
      }}
    >
      {card ? (
        hasImage ? (
          <>
            <div style={{ flex: 1, width: "100%", overflow: "hidden", background: "#F4EAD1" }}>
              <img
                src={card.image}
                alt={language === "fr" ? card.name_fr : card.name_en}
                crossOrigin="anonymous"
                referrerPolicy="no-referrer"
                style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
              />
            </div>
            <div
              style={{
                width: "100%",
                padding: "6px 8px",
                textAlign: "center",
                borderTop: "1px solid rgba(138,90,15,0.5)",
                background: "rgba(244,234,209,0.95)",
              }}
            >
              <div
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: `${dims.name}px`,
                  color: "#14103A",
                  lineHeight: 1.15,
                }}
              >
                {language === "fr" ? card.name_fr : card.name_en}
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                width: "100%",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: "#8A5A0F",
                fontStyle: "italic",
                fontSize: `${dims.roman}px`,
                fontFamily: "'Cormorant Garamond', serif",
              }}
            >
              <span>✧</span>
              <span>{card.roman}</span>
              <span>✧</span>
            </div>
            <div
              style={{
                fontSize: `${dims.glyph}px`,
                color: "#8A5A0F",
                lineHeight: 1,
                fontFamily: "'Cormorant Garamond', serif",
              }}
            >
              {card.suit_glyph || "☉"}
            </div>
            <div style={{ width: "100%", textAlign: "center" }}>
              <div
                style={{
                  width: "100%",
                  height: "1px",
                  background:
                    "linear-gradient(90deg, transparent, rgba(138,90,15,0.6), transparent)",
                  marginBottom: "6px",
                }}
              />
              <div
                style={{
                  fontFamily: "'Cormorant Garamond', serif",
                  fontSize: `${dims.name}px`,
                  color: "#14103A",
                  lineHeight: 1.15,
                }}
              >
                {language === "fr" ? card.name_fr : card.name_en}
              </div>
            </div>
          </>
        )
      ) : (
        <div
          style={{
            width: "100%",
            height: "100%",
            border: "1px dashed rgba(138,90,15,0.4)",
            borderRadius: "6px",
          }}
        />
      )}
    </div>
  );
};

export default ShareCard;
